import { useEffect, useState } from 'react';
import { Draggable, Droppable, DragDropContext, DropResult } from '@hello-pangea/dnd';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';

import { PickerColumn } from './data-list.types';

// ----------------------------------------------------------------------

interface DataListColumnPickerProps {
  /** Full ordered model (fixed + non-fixed) from buildPickerModel. */
  model: PickerColumn[];
  onSave: (model: PickerColumn[]) => void;
}

/**
 * "Columns" control for the shared list (JT1-1528): a popover that toggles column visibility and
 * drag-reorders columns, persisting on Apply. Fixed columns are shown locked and can't be dragged or
 * hidden. Cloned from export-field-mapper's @hello-pangea/dnd usage — the repo's DnD pattern.
 *
 * Edits are staged locally and only saved on Apply, so a mis-drag doesn't immediately repaint the
 * live list; Cancel/close reverts to the persisted layout.
 */
export default function DataListColumnPicker({ model, onSave }: DataListColumnPickerProps) {
  const { t } = useTranslate();
  const popover = usePopover();

  const [draft, setDraft] = useState<PickerColumn[]>(model);

  // Re-seed the draft whenever the popover opens or the persisted model changes.
  useEffect(() => {
    if (popover.open) setDraft(model);
  }, [popover.open, model]);

  const fixed = draft.filter((c) => c.fixed);
  const reorderable = draft.filter((c) => !c.fixed);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const next = Array.from(reorderable);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setDraft([...fixed, ...next]);
  };

  const toggle = (key: string) => {
    setDraft((prev) => prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  };

  const apply = () => {
    onSave(draft);
    popover.onClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Iconify icon="solar:settings-bold" />}
        onClick={popover.onOpen}
      >
        {t('common.columns', { defaultValue: 'Columns' })}
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 280, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
          {t('common.columns', { defaultValue: 'Columns' })}
        </Typography>

        {fixed.map((col) => (
          <Stack key={col.key} direction="row" alignItems="center" sx={{ px: 1, py: 0.5, opacity: 0.7 }}>
            <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
              <Iconify icon="solar:lock-keyhole-minimalistic-bold" width={16} />
            </Box>
            <Checkbox checked disabled size="small" />
            <Typography variant="body2">{col.label}</Typography>
          </Stack>
        ))}

        {fixed.length > 0 && reorderable.length > 0 && <Divider sx={{ my: 0.5 }} />}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="data-list-columns">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {reorderable.map((col, index) => (
                  <Draggable key={col.key} draggableId={col.key} index={index}>
                    {(dragProvided) => (
                      <Stack
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        direction="row"
                        alignItems="center"
                        sx={{ px: 1, py: 0.5 }}
                      >
                        <Box {...dragProvided.dragHandleProps} sx={{ width: 40, display: 'flex', justifyContent: 'center', cursor: 'grab' }}>
                          <Iconify icon="solar:hamburger-menu-linear" width={18} />
                        </Box>
                        <Checkbox checked={col.visible} size="small" onChange={() => toggle(col.key)} />
                        <Typography variant="body2">{col.label}</Typography>
                      </Stack>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 1, px: 1 }}>
          <Button size="small" color="inherit" onClick={popover.onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button size="small" variant="contained" onClick={apply}>
            {t('common.apply', { defaultValue: 'Apply' })}
          </Button>
        </Stack>
      </CustomPopover>
    </>
  );
}
